// usePinGate — PIN modal hook + Provider (ústava sek. 63)
// Backend rozhoduje BETA/OSTRÁ; UI vždy ukáže modal s kontextom, v BETA stačí potvrdiť bez PIN.
import React, { createContext, useContext, useState, useCallback } from 'react'
import { Modal } from '../components/Modal.jsx'
import { Btn, BtnAdd, BtnGhost } from '../components/Btn.jsx'
import { useToast } from '../components/Toast.jsx'
import { COLORS, TYPO, SPACE, RADIUS } from '../theme.js'
import { fetchAuth } from '../auth/fetchAuth.js'

const PinCtx = createContext(null)

// Popisné labely pre action_key — slovenčina, bez IT žargónu (ústava 12.0 bod 4a)
const ACTION_LABELS = {
  citlive_udaje_zamestnanca: {   // ZAMESTNANCI_OCHRANA_V1
    title: 'Zmeniť citlivé údaje zamestnanca',
    why: 'Dátum narodenia, rodné číslo a mzdové údaje v karte zamestnanca mení vedenie (admin, konateľ); ostatní len so schválením Mirka PIN-om (Mirko 27. 10. 2026). Vlastné citlivé údaje si nikto nemení.',
  },
  cp_zmazanie_odoslanej: {
    title: 'Odložiť odoslanú cenovú ponuku',
    why: 'Túto ponuku už zákazník dostal e-mailom a neskôr ju nahradila novšia verzia. Je to doklad o tom, čo sme poslali — zo zoznamu sa dá odložiť len so schválením PIN-om (ústava 63, 67.14). Neodoslaná ponuka sa maže bez PIN-u. Odložená ponuka sa nemaže z databázy: admin ju vie vrátiť v Logu.',
  },
  vyuctovanie_bez_nakladov: {
    title: 'Vyúčtovať bez potvrdených nákladov',
    why: 'Vyúčtovanie RD sa robí až po potvrdení nákladov (VSZAK a VSOST, mzdy, výmery, protokol od omietkára) — ústava 122.7. Výnimku schvaľuje admin alebo majiteľ PIN-om; schválenie platí 10 minút.',
  },
  kalkulacia_rozpad: {
    title: 'Otvoriť rozpad ceny',
    why: 'Rozpad ceny (naše náklady na m² po položkách) je normálne zatvorený. Otvoriť a meniť ho možno len so schválením PIN-om (ústava 63). Schválenie platí 10 minút pre túto kalkuláciu, potom sa rozpad sám zatvorí.',
  },
  cash_fa_over_400: {
    title: 'Cash faktúra nad €400',
    why: 'KAMIR pravidlo (DPH): faktúru dodávateľa nad €400 nemožno platiť v hotovosti, len ak je dodávateľ spriaznený SZČO partner (manželka, parťák, rodina). Ak je to výnimočná situácia, schvaľuje majiteľ PIN-om.',
  },
  velky_vydaj: {
    title: 'Veľký výdaj',
    why: 'Výdaj nad €8 000 musí potvrdiť majiteľ PIN-om (audit + zamedzenie omylu).',
  },
  mzda_dedup: {
    title: 'Duplicitná mzda',
    why: 'V mesiaci už existuje mzda pre tohto zamestnanca na tejto zákazke. Ak je dôvod (oprava, doplatok), schvaľuje admin/účtovníčka.',
  },
  cash_zaloha_office: {
    title: 'Office cash záloha',
    why: 'Office si nemôže sám vyplatiť zálohu — schvaľuje majiteľ (anti-fraud, ústava 12.2 C).',
  },
  hard_delete: {
    title: 'Natrvalo zmazať',
    why: 'Záznam sa zmaže natrvalo z databázy (výnimka ústavy 1 — len pre systémové duplikáty). Schvaľuje admin.',
  },
  vymery_regen: {
    title: 'Regenerovať štruktúru Výmer',
    why: 'Zákazka už má zadané výmery (ostré dáta). „Vygenerovať štruktúru" by mohla narobiť duplicity — preto ju potvrdzuje admin PIN-om. Bežné pridanie objektu/poschodia/bytu PIN nepotrebuje.',
  },
  vydaj_bez_dokladu: {
    title: 'Výdaj bez dokladu',
    why: 'Bez dokladu (pokladničný blok / faktúra) sa výdaj normálne neeviduje (ústava 11.15). Výnimka pre jednorazové prípady (upratovačka, drobnosti pri ceste) — schvaľuje admin/účtovník PIN-om. Operácia ide do auditu.',
  },
  uctovny_export: {
    title: 'Účtovný export (Na zaúčtovanie)',
    why: 'Zobrazenie a export bločkov s priradenými účtovnými kódmi pre účtovníčku (MKsoft import). Citlivé účtovné dáta — prístup len oprávnení (admin / office / účtovník).',
  },
  duplicita_dokladu: {
    title: 'Override duplicity dokladu',
    why: 'Systém našiel záznam s rovnakým IČO dodávateľa, číslom dokladu a sumou (ústava 13.2 — exaktná duplicita). Ak je dôvod (oprava, omyl), schvaľuje admin/účtovníčka.',
  },
}

export function PinProvider({ children }) {
  const [request, setRequest] = useState(null)  // { action_key, context, resolve }
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState(null)  // /api/pin/status response
  const toast = useToast()

  // Lazy načítanie statusu pri prvom otvorení
  const ensureStatus = useCallback(async () => {
    if (status) return status
    try {
      const r = await fetchAuth('/api/pin/status')
      if (r.ok) {
        const d = await r.json()
        setStatus(d)
        return d
      }
    } catch (_) {}
    return { mode: 'beta', has_pin: false, user_level: 9, user_role: '' }
  }, [status])

  const gate = useCallback((action_key, context = {}) => {
    return new Promise((resolve) => {
      ensureStatus().then(() => {
        setPin('')
        setRequest({ action_key, context, resolve })
      })
    })
  }, [ensureStatus])

  function close(result) {
    if (request && request.resolve) request.resolve(result)
    setRequest(null)
    setPin('')
    setBusy(false)
  }

  async function potvrd() {
    if (!request) return
    setBusy(true)
    try {
      const r = await fetchAuth('/api/pin/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pin: pin || null,
          action_key: request.action_key,
          context: request.context || {},
        }),
      })
      const d = await r.json()
      if (!r.ok || !d.ok) {
        toast.error(d.error || 'PIN nesedí')
        setBusy(false)
        return
      }
      toast.success(d.mode === 'beta' ? 'Schválené (BETA — bez PIN)' : 'Schválené')
      close({ ok: true, audit_id: d.audit_id, mode: d.mode, approved_by: d.approved_by })
    } catch (e) {
      toast.error('Chyba: ' + e.message)
      setBusy(false)
    }
  }

  const meta = request ? (ACTION_LABELS[request.action_key] || { title: request.action_key, why: '' }) : null
  const isBeta = status && status.mode === 'beta'

  return (
    <PinCtx.Provider value={gate}>
      {children}
      {request && (
        <Modal open title={meta.title} onClose={() => close({ ok: false, cancelled: true })} width={520}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
            {meta.why && (
              <div style={{
                padding: SPACE.md,
                background: 'rgba(245,158,11,0.08)',
                border: `1px solid #f59e0b`,
                borderRadius: RADIUS.md,
                fontSize: TYPO.sizes.sm,
              }}>
                <strong style={{ color: '#b45309' }}>Prečo PIN?</strong> {meta.why}
              </div>
            )}
            {request.context && Object.keys(request.context).length > 0 && (
              <div style={{ padding: SPACE.md, background: COLORS.bgSoft, borderRadius: RADIUS.md, fontSize: TYPO.sizes.sm }}>
                <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs, marginBottom: 4, textTransform: 'uppercase' }}>Detail</div>
                {Object.entries(request.context).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', gap: SPACE.sm, marginBottom: 2 }}>
                    <span style={{ color: COLORS.muted, minWidth: 110 }}>{k}:</span>
                    <span style={{ fontFamily: TYPO.mono }}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                  </div>
                ))}
              </div>
            )}
            {isBeta ? (
              <div style={{
                padding: SPACE.sm,
                background: 'rgba(59,130,246,0.08)',
                border: `1px solid ${COLORS.nav}`,
                borderRadius: RADIUS.sm,
                fontSize: TYPO.sizes.xs,
                color: COLORS.nav,
              }}>
                <strong>BETA režim</strong> — PIN sa zatiaľ nekontroluje, schvaľujem bez PIN-u. Operácia sa zapíše do auditu.
              </div>
            ) : (
              <label>
                <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4 }}>
                  PIN majiteľa / admina (4–8 znakov)
                </div>
                <input
                  type="password"
                  inputMode="numeric"
                  autoFocus
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') potvrd() }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `2px solid ${COLORS.line}`,
                    borderRadius: RADIUS.md,
                    fontSize: TYPO.sizes.lg,
                    fontFamily: TYPO.mono,
                    letterSpacing: '0.3em',
                    textAlign: 'center',
                    background: COLORS.bg,
                    color: COLORS.text,
                    boxSizing: 'border-box',
                  }}
                />
                {status && !status.has_pin && (
                  <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.remove, marginTop: 4 }}>
                    Nemáš nastavený PIN. Nastav si ho v Nastavenia → PIN.
                  </div>
                )}
              </label>
            )}
            <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
              <BtnGhost onClick={() => close({ ok: false, cancelled: true })} disabled={busy}>Zrušiť</BtnGhost>
              <BtnAdd onClick={potvrd} disabled={busy}>
                {busy ? 'Overujem…' : (isBeta ? 'Schváliť (BETA)' : 'Potvrdiť PIN')}
              </BtnAdd>
            </div>
          </div>
        </Modal>
      )}
    </PinCtx.Provider>
  )
}

export function usePinGate() {
  const ctx = useContext(PinCtx)
  if (!ctx) {
    // Fallback — ak provider chýba, hodíme chybu pri použití
    return async () => {
      console.error('[usePinGate] PinProvider chýba v stromu')
      return { ok: false, error: 'PinProvider not mounted' }
    }
  }
  return ctx
}
